import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, InputWrapper, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { getChangeRequest, splitChangeRequest, ChangeRequestSplitOptions } from '@/services/change-requests';
//import ChangeRequestChooseParticipants from './ChangeRequestChooseParticipants';
import theme from './ChangeRequestSplitDialog.module.scss';
import { ValidationScope, ValidatingInput, ValidatingTextArea, ValidationSummary } from '@/components/forms/validation';
import { Button } from '@/components/Buttons';
import { ChangedItem } from '@/types';
import { useValidationManager } from '@/utils/hooks';
import Banner from '@/components/Banner';

interface PartEditorProps {
    onRemoveClick(item: ChangeRequestSplitOptions): void;
    onChange(change: ChangedItem<ChangeRequestSplitOptions>): void;
    part: ChangeRequestSplitOptions;
}

const PartEditor: React.FC<PartEditorProps> = ({ part, onChange, onRemoveClick }) => {
    const triggerChange = (changes: Partial<ChangeRequestSplitOptions>) => {
        if (onChange) {
            onChange(ChangedItem.fromMerge(part, changes));
        }
    };

    // const handleInputChange = (e) => {
    //     const oldValue = part;
    //     const newValue = {
    //         ...oldValue,
    //         [e.target.name]: getInputValue(e.target),
    //     };

    //     // this.setState({
    //     //     part: newValue,
    //     // });

    //     triggerHandler(onChange, oldValue, newValue);
    // };

    // const deleteButton = (
    //     <Button color="secondary" onClick={(e) => triggerHandler(onRemoveClick, part)}>
    //         <Trans>Remove</Trans>
    //     </Button>
    // );

    return (
        <div className={theme.partEditor}>
            <ValidationScope>
                <div className="form-group">
                    <InputWrapper
                        appendContent={
                            !part.isOriginal ? (
                                <Button color="secondary" onClick={(e) => triggerHandler(onRemoveClick, part)}>
                                    <Trans>Remove</Trans>
                                </Button>
                            ) : null
                        }>
                        <ValidatingInput
                            required
                            type="text"
                            className="form-control"
                            value={part.subject}
                            name="subject"
                            onChange={(e) => triggerChange({ subject: e.target.value })}
                            placeholder={t`Subject`}
                        />
                    </InputWrapper>
                </div>

                <div className="form-group">
                    <ValidatingTextArea
                        required
                        rows={5}
                        className="form-control"
                        value={part.description}
                        name="description"
                        onChange={(e) => triggerChange({ description: e.target.value })}
                        fieldName={t`Description`}
                        placeholder={t`Enter description here...`}
                    />
                </div>
                <ValidationSummary />
            </ValidationScope>
        </div>
    );
};

interface ChangeRequestSplitDialogProps {
    changeRequestId: number;
    onClose(): void;
    onSave(): void;
    // onSave(changes: BatchedChanges<ChangeRequest>): void;
    show: boolean;
}

const ChangeRequestSplitDialog: React.FC<ChangeRequestSplitDialogProps> = ({ changeRequestId, show, onClose, onSave }) => {
    const [splitParts, setSplitParts] = useState<ChangeRequestSplitOptions[]>([]);
    const [saving, setSaving] = useState(false);

    const validationManager = useValidationManager([
        {
            isValid: (value, options) => splitParts.length > 1,
            message: ({ field }) => <Trans>At least two parts are required</Trans>,
            summaryMessage: ({ field }) => <Trans>At least two parts are required</Trans>,
        },
    ]);

    const handleOpen = async () => {
        setSplitParts([]);
        setSaving(false);

        var result = await getChangeRequest(changeRequestId);

        setSplitParts([
            { isOriginal: true, subject: result.subject, description: result.description },
            { isOriginal: false, subject: '', description: '' },
        ]);

        // getChangeRequest(changeRequestId).then((result) => {
        //     this.setState({
        //         changeRequest: result,
        //         splitParts: [
        //             { isOriginal: true, subject: result.subject, description: result.description },
        //             { isOriginal: false, subject: '', description: '' },
        //         ],
        //     });
        // });
    };

    const handleSaveClick = async () => {
        var validationErrors = validationManager.validate();

        if (validationErrors.length > 0) {
            return;
        }

        setSaving(true);

        try {
            await splitChangeRequest(changeRequestId, splitParts);
            // const changes = new BatchedChanges<ChangeRequest>(
            //     changedRequests.map((cr) => new Changes<ChangeRequest>(cr.id, cr))
            // );
            // triggerHandler(onSave, changes);
            triggerHandler(onSave);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const handlePartChange = (change: ChangedItem<ChangeRequestSplitOptions>) => {
        setSplitParts(change.updateArray(splitParts));
    };

    const handleAddPartClick = () => {
        setSplitParts([...splitParts, { isOriginal: false, subject: '', description: '' }]);
    };

    const handleRemoveClick = (part) => {
        setSplitParts(splitParts.filter((x) => x !== part));
    };

    const renderBody = () => {
        if (!splitParts) {
            return <LoadingIndicator />;
        }

        return (
            <div className={theme.dialogContent}>
                <ValidationScope validationManager={validationManager}>
                    <Banner type="info">
                        <Trans>
                            Splitting a request is used when the buyer has sent a request for 2 changes in the same request.
                            <br />
                            <strong>Do not</strong> split the request for providing different quotes from different
                            disciplines. <br />
                            <strong>Do not</strong> split the request to split the request between disciplines (eg.
                            electrician and plumber). <br />
                            <strong>Do</strong> Split the request when the buyer has asked for moving a wall in one room and
                            adding a bathtub in another.
                        </Trans>
                    </Banner>

                    <div>
                        {splitParts.map((part, index) => (
                            <PartEditor
                                key={index}
                                part={part}
                                onChange={handlePartChange}
                                onRemoveClick={handleRemoveClick}
                            />
                        ))}
                    </div>
                    {/* <ValidationSummary includeChildScopes={false} /> */}
                </ValidationScope>
            </div>
        );
    };

    return (
        <Modal isOpen={show} onOpen={handleOpen} toggle={handleCancelClick} size="lg">
            <ModalHeader>
                <Trans>Split request</Trans>
            </ModalHeader>
            <ModalBody>{renderBody()}</ModalBody>
            <ModalFooter>
                <Button color="link" className={theme.splitButton} onClick={handleAddPartClick}>
                    <Trans>Add new split</Trans>
                </Button>

                <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button color="primary" onClick={handleSaveClick} loading={saving}>
                    <Trans>Split into {splitParts.length} requests</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ChangeRequestSplitDialog;
